import Message from 'components/Message';
import { forEach, isArray, isObject } from 'lodash';
import { BodyType, BodyTypeEnum, DEFAULT_BODY_TYPE } from 'modules/apis/config';
import messageCodes from 'modules/apis/messageCode';
import { getResponse, IResponse } from 'modules/apis/Response';
import { setError } from 'reducers/error/function';
import { getToken } from 'reducers/token/function';
import { toSnakeCase } from 'utils/function/format';

const prepareData = (response: any): any => {
    let result = null as any;
    if (isArray(response)) {
        result = response.map((item: any) => prepareData(item));
    } else if (isObject(response)) {
        result = {};
        forEach(response, (value, key) => {
            if (key === 'file') {
                result[key] = value;
            } else {
                result[toSnakeCase(key)] = prepareData(value);
            }
        });
    } else {
        result = response;
    }
    return result;
};

interface IApiOptions {
    bodyType?: BodyTypeEnum;
    params?: any;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: any;
}

class ApiBase {
    private _baseUrl = 'https://portal-dev-api.clinicaldatas.net/' + '/api';

    get(
        url: string,
        options: IApiOptions = {},
        customHandleError?: (err: IResponse) => IResponse
    ) {
        return this.call(
            url,
            {
                ...options,
                method: 'GET',
            },
            customHandleError
        );
    }

    post = (
        url: string,
        body?: any,
        options: IApiOptions = {},
        customHandleError?: (err: IResponse) => IResponse
    ) => {
        const _body = this.createBody(
            body,
            options?.bodyType ?? DEFAULT_BODY_TYPE
        );
        return this.call(
            url,
            {
                ...options,
                body: _body || undefined,
                method: 'POST',
            },
            customHandleError
        );
    };

    put = (
        url: string,
        body?: any,
        options: IApiOptions = {},
        customHandleError?: (err: IResponse) => IResponse
    ) => {
        const _body = this.createBody(
            body,
            options?.bodyType ?? DEFAULT_BODY_TYPE
        );
        return this.call(
            url,
            {
                ...options,
                body: _body || undefined,
                method: 'PUT',
            },
            customHandleError
        );
    };

    delete = (
        url: string,
        options: IApiOptions = {},
        customHandleError?: (err: IResponse) => IResponse
    ) => {
        return this.call(
            url,
            {
                ...options,
                method: 'DELETE',
            },
            customHandleError
        );
    };

    call = async (
        pathUrl: string,
        rawOptions: IApiOptions,
        customHandleError?: (err: IResponse) => IResponse
    ) => {
        let url = this._baseUrl + pathUrl;
        if (rawOptions.params) {
            const paramsString = new URLSearchParams(
                prepareData(rawOptions.params)
            ).toString();
            url = url + '?' + paramsString;
        }
        try {
            let options = this.createHeader(rawOptions);
            let response = await fetch(url, {
                ...options,
            });
            const contentType = response.headers.get('content-type');
            if (contentType?.startsWith('application/json')) {
                const jsonData = await response.json();
                let data: IResponse = getResponse(jsonData);
                if (!data.isSuccess && customHandleError) {
                    data = customHandleError(data);
                }
                if (!data.isSuccess) {
                    return this.handleError(data);
                }
                return data;
            } else if (contentType === null) {
                const textData = await response.text();
                const statusCode = response.status;
                return getResponse({
                    error_code: statusCode === 200 ? 0 : statusCode,
                    data: textData,
                });
            } else {
                throw new Error(`Content type ${contentType} is not supported`);
            }
        } catch (error: any) {
            const e = error instanceof Error ? error : new Error(error);
            const response = getResponse(null, e);
            return this.handleError(response);
        }
    };

    createBody = (data: any, type: string) => {
        let formattedData = prepareData(data);
        switch (type) {
            case BodyType.JSON:
                return JSON.stringify(formattedData);
            case BodyType.FORM_DATA:
                // eslint-disable-next-line no-case-declarations
                let formData = new FormData();
                Object.keys(formattedData).forEach((key: string) => {
                    formData.append(key, formattedData[key] || '');
                });
                return formData;
            default:
                return data;
        }
    };

    createHeader = (options: any) => {
        if (!options.headers) {
            options.headers = { 'Content-Type': 'application/json' };
        }
        const token = getToken();
        if (token) {
            options.headers.Authorization = `Bearer ${token}`;
        }
        return options;
    };

    handleError = (error: IResponse) => {
        if ([401].includes(error.errorCode)) {
            setError(error.errorCode, error.message);
        }
        if (error.messageCode) {
            Message.sendError(
                messageCodes[error.messageCode] || messageCodes.default
            );
        }
        return error;
    };
}

export default ApiBase;
